import React, { useState, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";
import "./LegalAssistSignup.css";

interface LegalAssistSignupProps extends RouteComponentProps<any> {}

const LegalAssistSignup: React.FC<LegalAssistSignupProps> = (props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');

  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  const togglePassword = () => {
    setPwdType(pwdType === "password" ? "text" : "password");
  };

  const scrollToForm = () => {
    document.getElementById('la-signup-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);

    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const signupData: any = {
        email,
        password,
        mobileNumber,
        type: 'Mploi-er',
        is_legal_assist_signup: true,
        isAccountsSignup: true,
        is_accounts_signup: true,
        referred_by_code: affiliateCode,
        referredByCode: affiliateCode,
      };

      await userService.signup(signupData, affiliateCode, true);

      const loginResponse = await userService.login({ email, password });

      if (loginResponse.data && loginResponse.data.user && loginResponse.data.user.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');

        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        await userService.getUserData();
        window.location.href = 'https://simulation.mploi.com/legal';
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      if (error && Array.isArray(error) && error[0] === 'Email') {
        setApiError("Email already exists");
      } else {
        setApiError(
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred during signup."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };

  return (
    <Fragment>
      <Helmet>
        <title>MPLOI AI Legal Assist - Legal Expertise for Every Business</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Urbanist:wght@400;500;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="la-signup">
        {/* Navigation */}
        <nav className="la-nav">
          <img
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
            alt="MPLOI Logo"
            className="la-nav-logo"
            onClick={() => props.history.push("/")}
          />
          <ul className="la-nav-links">
            <li><a href="https://mploi.com/#about">About Us</a></li>
            <li><a href="https://mploi.com/#services">Services</a></li>
            <li><a href="https://mploi.com/blog">Blog</a></li>
            <li><a href="https://mploi.com/#pricing">Pricing</a></li>
            <li><a href="https://mploi.com/#contact">Contact</a></li>
          </ul>
          <div className="la-nav-actions">
            <button className="la-btn-login" onClick={() => props.history.push('/signin')}>Log In</button>
            <button className="la-btn-get-started" onClick={scrollToForm}>Get Started</button>
          </div>
        </nav>

        {/* Hero */}
        <section className="la-hero">
          <div className="la-hero-content">
            <h1>AI Legal<br/>Assist</h1>
            <p className="la-hero-tagline">One AI. Every legal question. Built for Australian businesses that can't afford to get it wrong.</p>
            <p className="la-hero-subtitle">
              Legal expertise shouldn't be a privilege reserved for businesses with deep pockets. 
              MPLOI's AI Legal Assist delivers instant, personalised legal guidance around the clock — 
              empowering businesses of every size to navigate complexity with confidence, cut consultation costs, and stay one step ahead.
            </p>
            <button className="la-hero-cta" onClick={scrollToForm}>
              Join NOW <span style={{fontSize: '20px'}}>↗</span>
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="la-content">
          <div className="la-content-inner">
            <span className="la-section-label">About Legal Assist</span>
            <h2>Legal expertise shouldn't be a privilege — it should be a given.</h2>
            <div className="la-content-body">
              <p>
                Every business faces legal challenges — contracts, compliance, workplace obligations, disputes — and the cost 
                of getting them wrong can be devastating. For small businesses, access to reliable legal advice has traditionally 
                meant expensive consultations for even the most straightforward questions.
              </p>
              <p>
                For larger organisations, legal teams are perpetually stretched, buried in routine queries that pull focus 
                from the high-value work that actually protects the business.
              </p>
              <p>
                <strong>MPLOI's AI Legal Assist changes that equation entirely.</strong>
              </p>
              <p>
                What sets our solution apart is what's powering it. We've built an explainable AI model specifically trained 
                on Australian legal frameworks, drawing continuously from the Federal Register of Legislation and the 
                Australasian Legal Information Institute — two of Australia's most authoritative and comprehensive legal data 
                sources. Unlike generic AI tools, our model is constantly retrained and updated to reflect the latest 
                legislative changes and legal developments, ensuring every response is grounded in current, locally relevant, 
                and jurisdictionally accurate information.
              </p>
              <p>
                Importantly, AI Legal Assist isn't designed to replace a legal specialist — and we're upfront about that. 
                What it does brilliantly is give you a trusted, knowledgeable sounding board so you understand exactly where 
                you stand before taking the next step and engaging a specialist. That clarity alone can save businesses 
                significant time, money, and costly missteps from the very outset.
              </p>
              <p>
                <strong>For smaller businesses, it's your virtual legal sidekick</strong> — cutting through complex jargon, 
                answering pressing questions instantly, and building a historical library of legal solutions tailored 
                specifically to your business. As you grow, it grows with you, offering scalable support that evolves 
                alongside your organisation's needs.
              </p>
              <p>
                <strong>For larger enterprises, it's a powerful force multiplier for your existing legal team.</strong> Juniors 
                and paralegals gain an exceptional training resource, routine queries are resolved instantly, and your senior 
                legal professionals reclaim the valuable hours needed to focus on complex, strategic matters that genuinely 
                require their expertise. With on-premise deployment options available for industries with strict security 
                requirements, AI Legal Assist adapts to your environment — not the other way around.
              </p>
              <p className="la-closing">
                One AI. Every legal question. Built for Australian businesses that can't afford to get it wrong.
              </p>
            </div>
          </div>
        </section>

        {/* Signup Form */}
        <section className="la-form-section" id="la-signup-form">
          <div className="la-form-container">
            <h2 className="la-form-title">Create Your Account</h2>

            <form onSubmit={handleSubmit} noValidate>
              <div className="la-form-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  className={`la-form-input ${btnClick && !isEmailValid ? 'error' : ''}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isEmailValid && <div className="la-form-error">Please enter a valid email address</div>}
              </div>

              <div className="la-form-group">
                <input
                  type="tel"
                  placeholder="Mobile Phone"
                  className={`la-form-input ${btnClick && !isMobileValid ? 'error' : ''}`}
                  value={mobileNumber}
                  onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isMobileValid && <div className="la-form-error">Please enter your mobile number</div>}
              </div>

              <div className="la-form-group">
                <input
                  type={pwdType}
                  placeholder="Password"
                  className={`la-form-input ${btnClick && !isPasswordValid ? 'error' : ''}`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                <div className="la-form-icon" onClick={togglePassword}>
                  <img
                    src={pwdType === "password" ?
                      UtilityFunctions.getImageURL() + 'eyeOff.png' :
                      UtilityFunctions.getImageURL() + 'eyeOn.png'}
                    alt="Toggle password"
                  />
                </div>
                {btnClick && !isPasswordValid && <div className="la-form-error">Password must meet the requirements below</div>}
              </div>

              <div className="la-form-group">
                <input
                  type="text"
                  placeholder="Affiliate Code (Optional)"
                  className="la-form-input"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  disabled={isLoading || !!localStorage.getItem("referralCode")}
                />
              </div>

              <ul className="la-validation-list">
                <li className={`la-validation-item ${password.length <= 0 ? '' : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.min_chars}
                </li>
                <li className={`la-validation-item ${password.length <= 0 ? '' : hasNumber.test(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.number_req}
                </li>
              </ul>

              {apiError && <div className="la-api-error">{apiError}</div>}

              <button
                type="submit"
                className="la-submit-btn"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <span><span className="la-spinner"></span>Creating Account...</span>
                ) : "GET STARTED NOW"}
              </button>

              <div className="la-form-footer">
                <p>
                  By signing up, you agree to our{" "}
                  <Link to={terms}>Terms of Service</Link>{" "}and{" "}
                  <Link to={privacy}>Privacy Policy</Link>
                </p>
                <p style={{marginTop: '16px'}}>
                  Already have an account?{" "}
                  <Link to={STRING_CONSTANTS.navigationString.login}>Sign In</Link>
                </p>
              </div>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="la-footer">
          <p>Questions? Contact our support team</p>
          <p><a href="mailto:info@mploi.com">info@mploi.com</a></p>
          <p style={{marginTop: '16px'}}>
            <Link to={privacy}>Privacy Policy</Link>{" | "}
            <Link to={terms}>Terms & Conditions</Link>
          </p>
          <p style={{marginTop: '24px', fontSize: '12px'}}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(LegalAssistSignup);
